import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export const FILTER_CHECK_ROW = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  fontSize: '0.72rem',
  cursor: 'pointer',
  color: '#334155',
  lineHeight: 1.35,
  padding: '3px 2px',
  margin: 0
};

const POPOVER_BOX = {
  background: '#fff',
  border: '1px solid #cbd5e1',
  borderRadius: 8,
  boxShadow: '0 10px 28px rgba(15,23,42,0.16)',
  padding: 8,
  boxSizing: 'border-box'
};

function clampPopoverPosition(rect, { width, align, maxHeight }) {
  const maxW = Math.min(width, Math.max(160, window.innerWidth - 16));
  let left = align === 'right' ? rect.right - maxW : rect.left;
  left = Math.min(Math.max(8, left), window.innerWidth - maxW - 8);
  let top = rect.bottom + 4;
  const estH = Math.min(maxHeight, window.innerHeight - 16);
  if (top + Math.min(240, estH) > window.innerHeight - 8) {
    top = Math.max(8, rect.top - 4 - Math.min(estH, Math.max(80, rect.top - 8)));
  }
  return { top, left, width: maxW };
}

export function useAnchoredPopover(open, anchorRef, { width = 280, align = 'left', maxHeight = 320 } = {}) {
  const [coords, setCoords] = useState(null);
  useLayoutEffect(() => {
    if (!open) {
      setCoords(null);
      return undefined;
    }
    const place = () => {
      const el = anchorRef.current;
      if (!el) return;
      setCoords(clampPopoverPosition(el.getBoundingClientRect(), { width, align, maxHeight }));
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, anchorRef, width, align, maxHeight]);
  return coords;
}

/**
 * Fixed menu aligned to a button. Grows left when align="right".
 * Portaled so a right-hand drawer cannot clip it.
 */
export function AnchoredPopover({
  open,
  anchorRef,
  width = 280,
  align = 'right',
  maxHeight = 360,
  zIndex = 80,
  children,
  onClose
}) {
  const portalRef = useRef(null);
  const coords = useAnchoredPopover(open, anchorRef, { width, align, maxHeight });

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (anchorRef.current?.contains(e.target)) return;
      if (portalRef.current?.contains(e.target)) return;
      if (typeof onClose === 'function') onClose();
    };
    const t = window.setTimeout(() => document.addEventListener('mousedown', onDoc), 0);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener('mousedown', onDoc);
    };
  }, [open, anchorRef, onClose]);

  if (!open || !coords || typeof document === 'undefined') return null;
  return createPortal(
    <div
      ref={portalRef}
      style={{
        position: 'fixed',
        top: coords.top,
        left: coords.left,
        width: coords.width,
        maxHeight,
        overflowY: 'auto',
        overflowX: 'hidden',
        zIndex,
        ...POPOVER_BOX
      }}
    >
      {children}
    </div>,
    document.body
  );
}

/**
 * Compact labeled button + popover. Same shape as Budget / Current:
 * a 24px summary button, checkbox/radio list inside.
 * fullWidth: name stacked above a full-width dropdown (right filter drawer).
 * Portaled when fullWidth (or portal) so the menu can grow left over the chart
 * and survive parent re-renders without being clipped.
 */
export default function FilterDropdown({
  label,
  summary,
  children,
  buttonWidth = 168,
  popoverWidth = 280,
  popoverMaxHeight = 320,
  align = 'left',
  active = false,
  title,
  fullWidth = false,
  portal,
  zIndex = 80,
  onOpen
}) {
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const close = useCallback(() => setOpen(false), []);
  const usePortal = portal != null ? portal : fullWidth;
  const popAlign = fullWidth && align === 'left' ? 'right' : align;

  useEffect(() => {
    if (!open || usePortal) return undefined;
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) close();
    };
    const t = window.setTimeout(() => document.addEventListener('mousedown', onDoc), 0);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener('mousedown', onDoc);
    };
  }, [open, usePortal, close]);

  const toggle = () => {
    setOpen((v) => {
      const next = !v;
      if (next && typeof onOpen === 'function') onOpen();
      return next;
    });
  };

  const button = (
    <button
      type="button"
      onClick={toggle}
      title={title || (typeof summary === 'string' ? summary : undefined)}
      style={{
        height: 24,
        width: fullWidth ? '100%' : buttonWidth,
        maxWidth: '100%',
        boxSizing: 'border-box',
        fontSize: '0.7rem',
        fontWeight: 600,
        borderRadius: 5,
        border: active || open ? '1px solid #0f766e' : '1px solid #d1d5db',
        background: active || open ? '#f0fdfa' : '#fff',
        color: '#334155',
        textAlign: 'left',
        cursor: 'pointer',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        padding: '0 8px'
      }}
    >
      {summary}
    </button>
  );

  const menu = usePortal ? (
    <AnchoredPopover
      open={open}
      anchorRef={boxRef}
      width={popoverWidth}
      align={popAlign}
      maxHeight={popoverMaxHeight}
      zIndex={zIndex}
      onClose={close}
    >
      {children}
    </AnchoredPopover>
  ) : (open ? (
    <div
      style={{
        position: 'absolute',
        zIndex: 80,
        top: '100%',
        left: popAlign !== 'right' ? 0 : 'auto',
        right: popAlign === 'right' ? 0 : 'auto',
        marginTop: 4,
        width: popoverWidth,
        maxWidth: `min(92vw, ${popoverWidth}px)`,
        maxHeight: popoverMaxHeight,
        overflowY: 'auto',
        overflowX: 'hidden',
        ...POPOVER_BOX
      }}
    >
      {children}
    </div>
  ) : null);

  if (fullWidth) {
    return (
      <div title={title} style={{ width: '100%', marginBottom: 8, minWidth: 0 }}>
        <div style={{
          fontSize: 10,
          fontWeight: 800,
          letterSpacing: 0.45,
          textTransform: 'uppercase',
          color: active ? '#0f766e' : '#64748b',
          marginBottom: 3
        }}
        >
          {label}
        </div>
        <div ref={boxRef} style={{ position: 'relative', width: '100%', minWidth: 0 }}>
          {button}
          {menu}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={boxRef}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 5,
        fontSize: '0.68rem',
        color: '#64748b',
        fontWeight: 600
      }}
    >
      <span onClick={toggle} style={{ cursor: 'pointer' }}>{label}</span>
      <div style={{ position: 'relative' }}>
        {button}
        {menu}
      </div>
    </div>
  );
}

export function FilterCheckList({
  items,
  selectedIds,
  onToggle,
  onSelectAll,
  search = true,
  empty = 'No values yet.',
  paint
}) {
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();
  const rows = (items || []).filter((item) => {
    if (!q) return true;
    return [item.id, item.label].some((x) => String(x || '').toLowerCase().includes(q));
  });
  const selected = new Set((selectedIds || []).map(String));
  const allOn = items.length > 0 && items.every((item) => selected.has(String(item.id)));

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        {search && (
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search…"
            style={{
              flex: 1,
              height: 24,
              boxSizing: 'border-box',
              border: '1px solid #cbd5e1',
              borderRadius: 4,
              padding: '0 8px',
              fontSize: '0.7rem'
            }}
          />
        )}
        {onSelectAll && (
          <button
            type="button"
            onClick={onSelectAll}
            style={{
              border: '1px solid #e2e8f0',
              background: '#f8fafc',
              borderRadius: 4,
              fontSize: '0.68rem',
              fontWeight: 700,
              color: '#334155',
              padding: '3px 8px',
              cursor: 'pointer'
            }}
          >
            {allOn ? 'Clear' : 'All'}
          </button>
        )}
      </div>
      {!items.length ? (
        <div style={{ fontSize: '0.68rem', color: '#94a3b8' }}>{empty}</div>
      ) : rows.length === 0 ? (
        <div style={{ fontSize: '0.68rem', color: '#94a3b8' }}>No match.</div>
      ) : rows.map((item) => {
        const faded = paint ? paint(item) : null;
        return (
          <label
            key={item.id}
            title={item.title || item.label}
            style={{ ...FILTER_CHECK_ROW, ...(faded || {}) }}
          >
            <input
              type="checkbox"
              checked={selected.has(String(item.id))}
              onChange={() => onToggle(item.id)}
              style={{ accentColor: item.color || '#64748b', width: 13, height: 13, flex: 'none' }}
            />
            {item.color && (
              <span style={{
                width: 8,
                height: 8,
                borderRadius: 99,
                background: item.color,
                flex: 'none'
              }}
              />
            )}
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {item.label}
            </span>
          </label>
        );
      })}
    </div>
  );
}

/**
 * Single-select list (radios). Use this instead of a nested <select>
 * so the first click on the toolbar button already shows the choices.
 */
export function FilterRadioList({
  groups,
  value,
  onChange,
  search = true,
  name = 'filter-radio'
}) {
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();
  const visible = (groups || []).map((group) => ({
    ...group,
    items: (group.items || []).filter((item) => {
      if (!q) return true;
      return [item.id, item.label, group.label].some((x) => String(x || '').toLowerCase().includes(q));
    })
  })).filter((group) => group.items.length);

  return (
    <div>
      {search && (
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search…"
          style={{
            width: '100%',
            height: 24,
            boxSizing: 'border-box',
            border: '1px solid #cbd5e1',
            borderRadius: 4,
            padding: '0 8px',
            fontSize: '0.7rem',
            marginBottom: 6
          }}
        />
      )}
      {!visible.length ? (
        <div style={{ fontSize: '0.68rem', color: '#94a3b8' }}>No match.</div>
      ) : visible.map((group) => (
        <div key={group.label || 'group'} style={{ marginBottom: 6 }}>
          {group.label ? (
            <div style={{ fontSize: '0.62rem', fontWeight: 700, color: '#94a3b8', letterSpacing: 0.3, margin: '4px 2px 2px' }}>
              {group.label}
            </div>
          ) : null}
          {group.items.map((item) => (
            <label key={item.id} style={FILTER_CHECK_ROW}>
              <input
                type="radio"
                name={name}
                value={item.id}
                checked={String(value) === String(item.id)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  if (String(value) !== String(item.id)) onChange(item.id);
                }}
                onChange={() => onChange(item.id)}
                style={{ accentColor: '#0f766e', width: 13, height: 13, flex: 'none' }}
              />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {item.label}
              </span>
            </label>
          ))}
        </div>
      ))}
    </div>
  );
}

export function pickCountSummary(selected, all, oneLabel) {
  if (!all.length) return 'None';
  if (!selected.length) return 'None';
  if (selected.length === all.length) return 'All';
  if (selected.length === 1) return oneLabel || String(selected[0]);
  return `${selected.length} selected`;
}
